import { TestPhase, TestPhaseStep, TestPhaseType } from "../../../testSpecification/testSpecificationApi";

export type ValidationStepsPromptInput = {
    testName: string;
    testDescription?: string;
    testMethodology?: string;
    phase: TestPhase;
};

export class ValidationStepsPrompt {
    static build({
        testName,
        testDescription,
        testMethodology,
        phase
    }: ValidationStepsPromptInput): string {
        const safeDescription = (testDescription ?? "").trim() || "(brak opisu)";
        const safeMethodology = (testMethodology ?? "").trim() || "(brak metodologii)";

        return `
Twoje zadanie:

Na podstawie nazwy testu, opisu testu oraz metodologii testu wygeneruj scenariusz
testu walidacyjnego w postaci kroków testowych wraz z kryteriami akceptacji.

Kroki testowe muszą:
- wynikać bezpośrednio z opisu i metodologii testu,
- być spójne z charakterem fazy walidacji (${phase.phaseType}),
- opisywać konkretne, możliwe do wykonania czynności testowe,
- nie powielać treści opisu ani metodologii,
- obejmować wyłącznie zakres zdefiniowany w opisie testu.

Kryteria akceptacji muszą:
- być jednoznaczne i mierzalne,
- jasno określać warunek zaliczenia kroku,
- nie zawierać interpretacji ani ogólników.

Wymagania ilościowe:
- zwróć od 1 do 5 kroków testowych,
- każdy krok musi zawierać co najmniej jedno kryterium akceptacji.

FORMAT ODPOWIEDZI (BARDZO WAŻNE):
Zwróć wynik WYŁĄCZNIE jako poprawny JSON w poniższym formacie:

[
  {
    "description": "opis pojedynczego kroku testowego",
    "acceptanceCriteria": [
      "pierwsze kryterium akceptacji",
      "drugie kryterium akceptacji"
    ]
  }
]

Nie dodawaj żadnego dodatkowego tekstu poza JSON.
Nie używaj komentarzy ani wyjaśnień.

Dane wejściowe:
- Nazwa testu: ${testName}
- Faza testu: ${phase.phaseType}
- Opis testu: ${safeDescription}
- Metodologia testu: ${safeMethodology}
`.trim();
    }
}

export const mapPhaseStepsToAiSteps = (steps: TestPhaseStep[]): AiStep[] => {
    return [...steps]
        .sort((a, b) => a.order - b.order)
        .map((s) => ({
            description: s.name.trim(),
            acceptanceCriteria: [...s.criterias]
                .sort((a, b) => a.order - b.order)
                .map((c) => c.name.trim())
                .filter(Boolean),
        }));
};

export type AiStep = {
    description: string;
    acceptanceCriteria: string[];
};

export type ValidationDescriptionPromptInput = {
    testName: string;
    phaseType: TestPhaseType;
    requirement: string | null
};

export class ValidationDescriptionPrompt {
    static build({ testName, phaseType, requirement }: ValidationDescriptionPromptInput): string {
        const intro = requirement
            ? `Na podstawie wymagania, nazwy testu oraz fazy testu wygeneruj zwięzły, techniczny opis testu walidacyjnego.`
            : 'Na podstawie nazwy testu oraz fazy testu wygeneruj zwięzły, techniczny opis testu walidacyjnego.'
        const reqInput = requirement
            ? `- Wymaganie: ${requirement}`
            : ''
        return `
Twoje zadanie:

${intro}

Opis powinien:
- jasno określać cel testu,
- opisywać zakres testu na poziomie ogólnym, bez wchodzenia w szczegóły wykonawcze,
- być spójny z charakterem podanej fazy walidacji (IQ / OQ / PQ / DQ),
- nie zawierać odniesień do konkretnych czynności, kroków testowych ani kryteriów akceptacji,
- być napisany neutralnym, formalnym językiem walidacyjnym.

FORMAT ODPOWIEDZI (BARDZO WAŻNE):
- Zwróć WYŁĄCZNIE tekst opisu testu jako string
- Treść powinna składać się z krótkiego akapitu wprowadzającego oraz listy punktowanej
- Do formatowania użyj wyłącznie prostych znaczników HTML (<p>, <ul>, <li>, <strong>)
- Bez JSON
- Bez nagłówków
- Bez dodatkowych komentarzy

Dane wejściowe:
${reqInput}
- Nazwa testu: ${testName}
- Faza testu: ${phaseType}
`.trim();
    }
}

export class ValidationMethodologyPrompt {
    static build({
        testName,
        phaseType,
        testDescription
    }: {
        testName: string;
        phaseType: string;
        testDescription?: string;
    }): string {
        const safeDescription = (testDescription ?? "").trim() || "(brak opisu)";

        return `
Twoje zadanie:

Na podstawie nazwy testu, fazy testu oraz opisu testu wygeneruj zwięzłą, techniczną metodologię testu walidacyjnego.

Metodologia powinna:
- opisywać sposób przeprowadzenia testu na poziomie ogólnym (podejście, typy weryfikacji, metody pomiaru/inspekcji, sposób rejestracji wyników),
- być spójna z charakterem podanej fazy walidacji (IQ / OQ / PQ / DQ),
- wynikać bezpośrednio z opisu testu i nie rozszerzać zakresu poza opis,
- nie zawierać kroków testowych ani sekwencji działań,
- możesz dodać sugestie dotyczące narzędzi lub norm, standardów, certyfikacji jeśli są istotne dla metodologii,
- być napisana neutralnym, formalnym językiem walidacyjnym.

FORMAT ODPOWIEDZI (BARDZO WAŻNE):
- Zwróć WYŁĄCZNIE tekst metodologii jako string
- Treść powinna składać się z krótkiego akapitu wprowadzającego oraz listy punktowanej
- Do formatowania użyj wyłącznie prostych znaczników HTML (<p>, <ul>, <li>, <strong>)
- Bez JSON
- Bez nagłówków
- Bez dodatkowych komentarzy

Dane wejściowe:
- Nazwa testu: ${testName}
- Faza testu: ${phaseType}
- Opis testu: ${safeDescription}
`.trim();
    }
}
